import React, { useState } from "react";
import {
  useWatch,
  useFieldArray,
  useFormContext,
  Controller,
} from "react-hook-form";

import "react-quill/dist/quill.snow.css";

import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { LayoutOptionSvg } from "../../../../../../components/DigitalReception/Layout";

import QuillWithSocketUpload from "../../../../../../components/HtmlEditor";

import FileUploadProvider from "../../../../../../components/FileUpload";
import ColorPicker from "../../../../../../components/ColorPicker";

const MemoizedQuill = React.memo(QuillWithSocketUpload);

const SortableItem = ({
  id,
  label,
  onEdit,
  onRemove,
}: {
  id: string;
  label: string;
  onEdit: () => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation(["common"]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8,
        padding: 8,
        borderRadius: 4,
        backgroundColor: "#f8f9fa",
        border: "1px solid #ddd",
        userSelect: "none",
      }}
    >
      <div {...attributes} {...listeners} style={{ cursor: "grab", flex: 1 }}>
        <strong>{label || "—"}</strong>
      </div>

      <div>
        <Button
          variant="outline-primary"
          size="sm"
          className="mr-2"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          {t("common:edit")}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          {t("common:remove")}
        </Button>
      </div>
    </div>
  );
};

const LayoutWayfindingOptions = (props: {
  onChangePreview: (name: string, value: any) => any;
  availableSvgs?: number[];
  language?: string;
}) => {
  const { onChangePreview, availableSvgs, language } = props;
  const { t } = useTranslation(["screens", "common"]);
  const { control, getValues } = useFormContext();

  const enabledMultiWayfinding = useWatch({
    name: "multiWayfinding.enabled",
    control,
  });

  const {
    fields: wayfindingOptions,
    append,
    update,
    move,
    remove,
  } = useFieldArray<
    IDigitalReceptionLayout,
    "multiWayfinding.wayfindingOptions",
    "id"
  >({
    control,
    name: "multiWayfinding.wayfindingOptions",
  });

  const [editIndex, setEditIndex] = useState<number | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleSave = () => {
    if (editIndex === null) return;
    const values = getValues(`multiWayfinding.wayfindingOptions.${editIndex}`);
    update(editIndex, values);
    setEditIndex(null);
  };

  const onChangeWayfindingPreview = () => {
    const updatedOptions = getValues("multiWayfinding.wayfindingOptions");
    onChangePreview("multiWayfinding.wayfindingOptions", updatedOptions);
  };

  const handleAdd = () => {
    const newOption = {
      label: "",
      instructionLabel: "",
      svg: 1001,
      colors: { background: undefined },
    };

    append(newOption);

    const updatedOptions = [...wayfindingOptions, newOption];
    const newIndex = updatedOptions.length - 1;

    setEditIndex(newIndex);

    onChangePreview("multiWayfinding.wayfindingOptions", updatedOptions);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = wayfindingOptions.findIndex((i) => i.id === active.id);
    const newIndex = wayfindingOptions.findIndex((i) => i.id === over.id);

    move(oldIndex, newIndex);

    onChangeWayfindingPreview();
  };

  if (!enabledMultiWayfinding) return null;

  return (
    <div>
      <h6 className="mb-2">
        {t("screens:screen.settings.layout.multiWayfinding.options.title")}
      </h6>
      {wayfindingOptions.length > 0 && editIndex === null && (
        <p className="mb-3">
          {t("screens:screen.settings.layout.multiWayfinding.options.subtitle")}
        </p>
      )}
      {editIndex === null ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={wayfindingOptions.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            {wayfindingOptions.map((field, index) => (
              <SortableItem
                key={field.id}
                id={field.id}
                label={field.label}
                onEdit={() => setEditIndex(index)}
                onRemove={() => {
                  remove(index);

                  onChangeWayfindingPreview();
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        wayfindingOptions.map((field, index) => {
          if (index !== editIndex) return null;

          return (
            <div
              key={field.id}
              className="mb-3 border p-3 rounded bg-light position-relative"
            >
              <div className="top-0 right-0 position-absolute m-3">
                <Button variant="primary" size="sm" onClick={handleSave}>
                  {t("common:close")}
                </Button>
              </div>
              <div className="form-group mb-2 mt-3">
                <label className="form-label">
                  {t(
                    "screens:screen.settings.layout.multiWayfinding.options.text",
                  )}
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      {language?.toUpperCase() ?? "EN"}
                    </span>
                  </div>
                  <Controller
                    name={`multiWayfinding.wayfindingOptions.${index}.label`}
                    render={({ field: { onChange, ...props } }) => (
                      <input
                        onChange={(value) => {
                          onChange(value);

                          onChangeWayfindingPreview();
                        }}
                        {...props}
                        className="form-control"
                      />
                    )}
                  />
                </div>
              </div>
              {/* <Controller
                name={`multiWayfinding.wayfindingOptions.${index}.colors.text`}
                render={({ field: { value, onChange } }) => (
                  <div className="form-group mb-2">
                    <ColorPicker
                      label={t(
                        "screens:screen.settings.layout.multiWayfinding.options.textColor",
                      )}
                      value={value || undefined}
                      onChange={(value) => {
                        onChange(value);

                        onChangeWayfindingPreview();
                      }}
                    />
                  </div>
                )}
              /> */}
              <Controller
                name={`multiWayfinding.wayfindingOptions.${index}.colors.background`}
                render={({ field: { value, onChange } }) => (
                  <div className="form-group mb-3">
                    <ColorPicker
                      label={t(
                        "screens:screen.settings.layout.multiWayfinding.options.backgroundColor",
                      )}
                      value={value || undefined}
                      onChange={(value) => {
                        onChange(value);

                        onChangeWayfindingPreview();
                      }}
                    />
                  </div>
                )}
              />
              <LayoutOptionSvg
                name={`multiWayfinding.wayfindingOptions.${index}`}
                label={t(
                  "screens:screen.settings.layout.multiWayfinding.options.svg",
                )}
                onChangePreview={onChangeWayfindingPreview}
                availableSvgs={availableSvgs}
              />

              <div className="form-group mb-2">
                <label className="form-label">
                  {t(
                    "screens:screen.settings.layout.multiWayfinding.options.instruction",
                  )}
                </label>
                <Controller
                  control={control}
                  name={`multiWayfinding.wayfindingOptions.${index}.instructionLabel`}
                  defaultValue={field.instructionLabel || ""}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FileUploadProvider>
                        <MemoizedQuill
                          value={value || ""}
                          onChange={(content) => {
                            onChange(content);
                            onChangeWayfindingPreview();
                          }}
                        />
                      </FileUploadProvider>
                    );
                  }}
                />
              </div>
              {/* <div className="mt-3 text-right">
                <Button variant="primary" size="sm" onClick={handleSave}>
                  {t("common:close")}
                </Button>
              </div> */}
            </div>
          );
        })
      )}

      {editIndex === null && (
        <button className="btn btn-secondary mt-3" onClick={handleAdd}>
          {t("screens:screen.settings.layout.multiWayfinding.options.add")}
        </button>
      )}
    </div>
  );
};

export default LayoutWayfindingOptions;
